import { useState, useEffect } from 'react';

export const useAnnouncementHandlers = ({
  onExpand,
  onCollapse,
  setAnnouncementsExpanded,
  updateAnnouncement,
  organizations,
  containerRef,
  announcementsExpanded,
  announcements,
  setUpdateCounter,
}) => {
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [tempContent, setTempContent] = useState({});
  const [showDetails, setShowDetails] = useState(null);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [justSaved, setJustSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const updateContent = (path, value) => {
    setJustSaved(false);
    setIsSaving(false);
    setTempContent(prev => {
      const result = { ...prev };
      result[path] = value;
      return result;
    });
  };

  const handleSave = async (announcementData) => {
    try {
      setIsSaving(true);
      
      // Create the data to save, combining tempContent with the announcement data
      const dataToSave = {
        ...announcementData,
        title: tempContent.title || announcementData.title,
        description: tempContent.description || announcementData.description,
        content: announcementData.content
      };

      const result = await updateAnnouncement(dataToSave);
      
      if (result.error) throw result.error;
      
      if (!result.data || !result.data.id) {
        throw new Error('Invalid response data');
      }

      // Update both editing announcement and temp content with the latest data
      const savedData = result.data;
      
      // Don't update the content in editingAnnouncement to preserve editor state
      setEditingAnnouncement({
        ...savedData,
        content: announcementData.content
      });

      setTempContent({
        title: savedData.title,
        description: savedData.description,
        image_url: savedData.image_url,
        type: savedData.type,
        content: announcementData.content
      });
      
      // Trigger a re-fetch by incrementing the counter
      setUpdateCounter(c => c + 1);
      
      setJustSaved(true);
      
      setTimeout(() => {
        setIsSaving(false);
        setJustSaved(false);
      }, 1000);

      return { data: savedData, error: null };
    } catch (error) {
      console.error('Failed to save announcement:', error);
      setIsSaving(false);
      return { data: null, error };
    }
  };

  const handleDelete = async (announcement) => {
    try {
      setIsSaving(true);
      const result = await updateAnnouncement({
        id: announcement.id,
        organization_id: announcement.organization_id,
        _delete: true
      });

      if (result.error) throw result.error;
      
      setEditingAnnouncement(null);
      setTempContent({});
      setIsSaving(false);
      
      // Trigger a re-fetch after delete
      setUpdateCounter(c => c + 1);

      return { data: result.data, error: null };
    } catch (error) {
      setIsSaving(false);
      console.error('Failed to delete announcement:', error);
      return { data: null, error };
    }
  };

  const handleExpandClick = (id) => {
    if (editingAnnouncement) return;

    if (!expandedId) {
      setSavedScrollPosition(containerRef.current?.scrollTop || 0);
      onExpand();
    } else {
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = savedScrollPosition;
        }
      }, 0);
    }
    setExpandedId(expandedId === id ? null : id);
    if (expandedId === id) {
      onCollapse();
    }
  };

  const handleAnnouncementClick = (id) => {
    if (editingAnnouncement) return;
    setShowDetails(showDetails === id ? null : id);
  };

  const handleEditClick = (e, announcement) => {
    e.stopPropagation();
    setJustSaved(false);
    setIsSaving(false);
    setEditingAnnouncement(announcement);
    setTempContent({
      title: announcement.title,
      description: announcement.description,
      image_url: announcement.image_url || '',
      type: announcement.type,
      content: {
        ...announcement.content,
        raw: announcement.content?.raw,
        html: announcement.content?.html,
        text: announcement.content?.text,
        isDraft: announcement.content?.isDraft
      }
    });
  };

  const handleAddAnnouncementClick = (e) => {
    e.stopPropagation();
    setJustSaved(false);
    setIsSaving(false);
    
    const newAnnouncement = {
      id: 'new',
      title: '',
      description: '',
      image_url: '',
      organization_id: organizations?.admin?.[0]?.organizationId,
      type: 'update',
      content: {}
    };
    
    setEditingAnnouncement(newAnnouncement);
    setTempContent({
      title: '',
      description: '',
      image_url: '',
      type: 'update',
      content: {}
    });
    onExpand();
  };

  const handleCancel = () => {
    setEditingAnnouncement(null);
    setTempContent({});
    setJustSaved(false);
    setIsSaving(false);
  };

  return {
    editingAnnouncement,
    tempContent,
    showDetails,
    expandedId,
    justSaved,
    isSaving,
    setEditingAnnouncement,
    setExpandedId,
    updateContent,
    handleSave,
    handleDelete,
    handleExpandClick,
    handleAnnouncementClick,
    handleEditClick,
    handleAddAnnouncementClick,
    handleCancel,
  };
}; 